import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TableBody,
  TableCell,
  TableRow,
  TextField
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ConfirmButton, {
  ConfirmButtonTransitionState
} from "@saleor/components/ConfirmButton";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import useSearchQuery from "@saleor/hooks/useSearchQuery";
import { buttonMessages } from "@saleor/intl";
import { SearchLanguages_search_edges_node } from "@saleor/searches/types/SearchLanguages";
import useScrollableDialogStyle from "@saleor/styles/useScrollableDialogStyle";
import { FetchMoreProps } from "@saleor/types";
import React from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { FormattedMessage, useIntl } from "react-intl";

import Checkbox from "../Checkbox";

export interface FormData {
  languages: SearchLanguages_search_edges_node[];
  query: string;
}

const useStyles = makeStyles(
  {
    avatar: {
      "&&:first-child": {
        paddingLeft: 0
      },
      width: 72
    },
    checkboxCell: {
      paddingLeft: 0,
      width: 88
    },
    colName: {
      paddingLeft: 0
    }
  },
  { name: "AssignLanguageDialog" }
);

export interface AssignLanguageDialogProps extends FetchMoreProps {
  confirmButtonState: ConfirmButtonTransitionState;
  open: boolean;
  languages: SearchLanguages_search_edges_node[];
  loading: boolean;
  onClose: () => void;
  onFetch: (value: string) => void;
  onSubmit: (data: SearchLanguages_search_edges_node[]) => void;
}

function handleLanguageAssign(
  language: SearchLanguages_search_edges_node,
  isSelected: boolean,
  selectedLanguages: SearchLanguages_search_edges_node[],
  setSelectedLanguages: (data: SearchLanguages_search_edges_node[]) => void
) {
  if (isSelected) {
    setSelectedLanguages(
      selectedLanguages.filter(
        selectedLanguage => selectedLanguage.id !== language.id
      )
    );
  } else {
    setSelectedLanguages([...selectedLanguages, language]);
  }
}

const scrollableTargetId = "assignLanguageScrollableDialog";

const AssignLanguageDialog: React.FC<AssignLanguageDialogProps> = props => {
  const {
    confirmButtonState,
    hasMore,
    open,
    loading,
    languages,
    onClose,
    onFetch,
    onFetchMore,
    onSubmit
  } = props;
  const classes = useStyles(props);
  const scrollableDialogClasses = useScrollableDialogStyle({});

  const intl = useIntl();
  const [query, onQueryChange] = useSearchQuery(onFetch);
  const [selectedLanguages, setSelectedLanguages] = React.useState<
    SearchLanguages_search_edges_node[]
  >([]);

  const handleSubmit = () => onSubmit(selectedLanguages);

  return (
    <Dialog
      onClose={onClose}
      open={open}
      classes={{ paper: scrollableDialogClasses.dialog }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        <FormattedMessage
          defaultMessage="Assign Language"
          description="dialog header"
        />
      </DialogTitle>
      <DialogContent className={scrollableDialogClasses.topArea}>
        <TextField
          disabled={true}
          name="query"
          value={query}
          onChange={onQueryChange}
          label={intl.formatMessage({
            defaultMessage: "Search Languages"
          })}
          placeholder={intl.formatMessage({
            defaultMessage:
              "Search by language name, attribute, language type etc..."
          })}
          fullWidth
          InputProps={{
            autoComplete: "off",
            endAdornment: loading && <CircularProgress size={16} />
          }}
        />
      </DialogContent>
      <DialogContent
        className={scrollableDialogClasses.scrollArea}
        id={scrollableTargetId}
      >
        <InfiniteScroll
          dataLength={languages?.length}
          next={onFetchMore}
          hasMore={hasMore}
          scrollThreshold="100px"
          loader={
            <div className={scrollableDialogClasses.loadMoreLoaderContainer}>
              <CircularProgress size={16} />
            </div>
          }
          scrollableTarget={scrollableTargetId}
        >
          <ResponsiveTable key="table">
            <TableBody>
              {languages &&
                languages.map(language => {
                  const isSelected = selectedLanguages.some(
                    selectedLanguage => selectedLanguage.id === language.id
                  );

                  return (
                    <TableRow
                      key={language.id}
                      data-test-id="assign-language-table-row"
                    >
                      <TableCell className={classes.colName}>
                        {language.language}
                      </TableCell>
                      <TableCell className={classes.colName}>
                        {language.languageCode}
                      </TableCell>
                      <TableCell
                        padding="checkbox"
                        className={classes.checkboxCell}
                      >
                        <Checkbox
                          checked={isSelected}
                          onChange={() =>
                            handleLanguageAssign(
                              language,
                              isSelected,
                              selectedLanguages,
                              setSelectedLanguages
                            )
                          }
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </ResponsiveTable>
        </InfiniteScroll>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          <FormattedMessage {...buttonMessages.back} />
        </Button>
        <ConfirmButton
          data-test="submit"
          transitionState={confirmButtonState}
          color="primary"
          variant="contained"
          type="submit"
          onClick={() => {
            setSelectedLanguages([]);
            handleSubmit();
          }}
        >
          <FormattedMessage
            defaultMessage="Assign languages"
            description="button"
          />
        </ConfirmButton>
      </DialogActions>
    </Dialog>
  );
};
AssignLanguageDialog.displayName = "AssignLanguageDialog";
export default AssignLanguageDialog;
